import type { BulkTask, EtaEnvironment } from '../../../api/types';
import { TestEnvStrip } from '../../../app/shell/TestEnvStrip';
import { useI18n } from '../../../i18n/I18nProvider';
import { UserText } from '../../../ui/Bidi';
import { ConfirmDialog } from '../../../ui/Dialog';
import { Money } from '../../../ui/Layout';
import salesStyles from '../Sales.module.css';
import { MockLabel } from '../status';
import { runLabel } from './text';

/**
 * The batch page's dialogs (BLOCK-1B-BULK-DESIGN §4, §5; AC-B9): a sentence and a short list of
 * facts — never a table, a list of documents, an input or an "apply to all" control, and never a
 * number in the title. The confirm button carries the run button's label.
 */

export interface RunFacts {
  /** How many documents will run. */
  count: number;
  /** The send task's total in EGP (the sum of the rows that will run). */
  total: string | null;
}

export function SendConfirm({
  isOpen,
  onOpenChange,
  onConfirm,
  facts,
  organization,
  environment,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  facts: RunFacts;
  organization: string | null;
  environment: EtaEnvironment | undefined;
}) {
  const { t, formatNumber } = useI18n();
  return (
    <ConfirmDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={t.batch.confirm.sendTitle}
      confirmLabel={runLabel(t, 'send')}
      confirmVariant="primary"
      onConfirm={onConfirm}
    >
      <TestEnvStrip compact />
      <p>{t.batch.confirm.sendBody}</p>
      <dl className={salesStyles.kv} data-testid="batch-confirm-summary">
        <dt>{t.editor.confirm.organization}</dt>
        <dd>
          <UserText>{organization ?? '—'}</UserText>
        </dd>
        <dt>{t.editor.confirm.environment}</dt>
        <dd>
          {environment ? t.env[environment] : '—'} <MockLabel environment={environment} />
        </dd>
        <dt>{t.batch.confirm.documents}</dt>
        <dd data-testid="batch-confirm-count" data-count={facts.count}>
          {formatNumber(String(facts.count))}
        </dd>
        <dt>{t.sales.columns.amount}</dt>
        <dd>
          <Money value={facts.total} currency="EGP" />
        </dd>
      </dl>
    </ConfirmDialog>
  );
}

export function DiscardConfirm({
  isOpen,
  onOpenChange,
  onConfirm,
  facts,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  facts: RunFacts;
}) {
  const { t, formatNumber } = useI18n();
  return (
    <ConfirmDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={t.batch.confirm.discardTitle}
      confirmLabel={runLabel(t, 'discard')}
      confirmVariant="danger"
      onConfirm={onConfirm}
    >
      <p>{t.batch.confirm.discardBody}</p>
      <dl className={salesStyles.kv} data-testid="batch-confirm-summary">
        <dt>{t.batch.confirm.documents}</dt>
        <dd data-testid="batch-confirm-count" data-count={facts.count}>
          {formatNumber(String(facts.count))}
        </dd>
      </dl>
    </ConfirmDialog>
  );
}

/**
 * «إعادة محاولة ما لم يكتمل؟» (RB4-02): the rows are marked in the results table, not listed here.
 * A send retry shows the same facts as the first confirmation (RIB-05): organization, environment
 * with MOCK, the count and the total of the rows marked «سيُنفَّذ».
 */
export function RetryConfirm({
  isOpen,
  onOpenChange,
  onConfirm,
  task,
  count,
  total = null,
  organization = null,
  environment,
  isPhone = false,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  task: BulkTask;
  count: number;
  /** Send: the EGP total of the rows that will run. */
  total?: string | null;
  organization?: string | null;
  environment?: EtaEnvironment | undefined;
  /** RIM-07: on a phone the marked rows lie under the dialog: no sentence points at them. */
  isPhone?: boolean;
}) {
  const { t, formatNumber } = useI18n();
  return (
    <ConfirmDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={t.batch.retryTitle}
      confirmLabel={t.batch.results.retry}
      confirmVariant={task === 'discard' ? 'danger' : 'primary'}
      onConfirm={onConfirm}
    >
      {task === 'send' ? <TestEnvStrip compact /> : null}
      <p>
        {t.batch.retryBody}
        {isPhone ? null : ` ${t.batch.retryPointer}`}
      </p>
      <dl className={salesStyles.kv} data-testid="batch-confirm-summary">
        {task === 'send' ? (
          <>
            <dt>{t.editor.confirm.organization}</dt>
            <dd>
              <UserText>{organization ?? '—'}</UserText>
            </dd>
            <dt>{t.editor.confirm.environment}</dt>
            <dd>
              {environment ? t.env[environment] : '—'} <MockLabel environment={environment} />
            </dd>
          </>
        ) : null}
        <dt>{t.batch.confirm.documents}</dt>
        <dd data-testid="batch-confirm-count" data-count={count}>
          {formatNumber(String(count))}
        </dd>
        {task === 'send' ? (
          <>
            <dt>{t.sales.columns.amount}</dt>
            <dd data-testid="batch-confirm-total">
              <Money value={total} currency="EGP" />
            </dd>
          </>
        ) : null}
      </dl>
    </ConfirmDialog>
  );
}

/**
 * «ترك نتائج هذا التنفيذ؟» (RIB-01, DEC-046): asked before a new batch or a new task leaves results
 * that are not settled — rows with the Tax Authority now, not completed, or not confirmed.
 */
export function LeaveResultsConfirm({
  isOpen,
  onOpenChange,
  onConfirm,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  const { t } = useI18n();
  return (
    <ConfirmDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={t.batch.leave.title}
      confirmLabel={t.batch.leave.confirm}
      confirmVariant="primary"
      onConfirm={onConfirm}
    >
      <p>{t.batch.leave.body}</p>
    </ConfirmDialog>
  );
}

/** «إفراغ الحزمة؟»: every document leaves the batch; no document changes. */
export function ClearConfirm({
  isOpen,
  onOpenChange,
  onConfirm,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  const { t } = useI18n();
  return (
    <ConfirmDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={t.batch.rail.clearTitle}
      confirmLabel={t.batch.rail.clearConfirm}
      confirmVariant="danger"
      onConfirm={onConfirm}
    >
      <p>{t.batch.rail.clearBody}</p>
    </ConfirmDialog>
  );
}
